import React, { createRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { selectConversations } from '../../redux/reducers/conversationReducer';
import { Conversation } from '../../Types/conversation';
import { CardSize } from '../../Types/enums';
import Card from '../Card/Card';
import ChatOptionsMenu from './ChatOptionsMenu';

type ChatCardProps = {
  conversation: Conversation;
  searchQuery: string;
  IsSearchFocused: boolean;
  onConversationCardClick: (
    conversationId?: string,
    projectId?: string,
    shouldFetchConversations?: boolean
  ) => void;
  refetchConversations?: () => void;
  size?: CardSize;
};

const ChatCard = ({
  onConversationCardClick,
  conversation,
  searchQuery,
  IsSearchFocused,
  refetchConversations,
  size = CardSize.MEDIUM,
}: ChatCardProps) => {
  const { currentConversation } = useSelector(selectConversations);
  const currentConversationCardRef = createRef<HTMLDivElement>();

  const isElementInView = (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  useEffect(() => {
    const getScrollParent = (element: HTMLElement): HTMLElement | null => {
      if (!element) {
        return document.documentElement;
      }

      const isElementScrollable = element.scrollHeight > element.clientHeight;
      if (isElementScrollable) {
        return element;
      }

      return element.parentElement ? getScrollParent(element.parentElement) : null;
    };

    const element = currentConversationCardRef.current;
    if (element && !isElementInView(element) && !IsSearchFocused) {
      // We use a setTimeout here to push this DOM update to the next event loop,
      // ensuring it doesn't get overriden
      setTimeout(() => {
        if (!isElementInView(element)) {
          const elementRect = element.getBoundingClientRect();
          const scrollParent = getScrollParent(element);

          if (scrollParent) {
            const targetScrollTop =
              elementRect.top +
              scrollParent.scrollTop -
              scrollParent.clientHeight / 2 +
              elementRect.height / 2;

            scrollParent.scrollTo({
              top: targetScrollTop,
            });
          }
        }
      }, 100);
    }
  }, [currentConversation, currentConversationCardRef, IsSearchFocused]);

  const isCurrentlySelected = currentConversation?.id === conversation.id;

  return (
    <Card
      type="conversation"
      title={conversation.title}
      searchQuery={searchQuery}
      selected={isCurrentlySelected}
      pinned={conversation.pinned}
      matchingMessage={conversation.matchingMessage}
      CardContextMenu={
        <ChatOptionsMenu conversation={conversation} refetchConversations={refetchConversations} />
      }
      onCardClick={onConversationCardClick}
      time={conversation.lastActivityAt}
      folderName={conversation.folderName ?? ''}
      hasFileUploaded={conversation.fileUpload === true}
      ref={isCurrentlySelected ? currentConversationCardRef : undefined}
      isPrivate={conversation.isPrivate}
      tools={conversation.toolsUsed}
      size={size}
    />
  );
};

export default ChatCard;
